import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/kaluki-528-ibiza-31-may-2026";
const IMAGE_URL = "https://www.hey-lets-travel.com/assets/community/8273b509-c413-4b18-89fc-059814fb796c.png";
const TICKET_URL = "https://www.ibiza-spotlight.com/night/promoters/kaluki-528";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const LINEUP = [
  "Julian Fijma",
  "Kepler B2B Burnski",
  "Robbie Doherty",
  "Tommy Phillips",
  "Pirate Copy",
];

const KEYWORDS = [
  "Kaluki Ibiza 2026",
  "Kaluki 528 Ibiza tickets",
  "528 Ibiza Kaluki",
  "Kaluki Ibiza Sunday 31 May",
  "Julian Fijma Ibiza",
  "Kepler B2B Burnski Ibiza",
  "Robbie Doherty Ibiza",
  "Tommy Phillips Ibiza",
  "Pirate Copy Ibiza",
  "Ibiza tech house events 2026",
  "Ibiza house music events 2026",
  "528 Ibiza events",
  "San Antonio Ibiza club events",
  "Ibiza open air parties 2026",
  "HLT Ibiza concierge",
  "Ibiza hotel and ticket package",
  "Ibiza party holiday 2026",
];

const FAQS = [
  {
    question: "When is Kaluki at 528 Ibiza?",
    answer: "Kaluki at 528 Ibiza takes place on Sunday 31 May 2026, running from 17:00 to 23:00.",
  },
  {
    question: "Where is Kaluki Ibiza taking place?",
    answer: "Kaluki takes place at 528 Ibiza, an open-air Ibiza venue close to San Antonio.",
  },
  {
    question: "Who is playing Kaluki at 528 Ibiza?",
    answer: "The listed lineup includes Julian Fijma, Kepler B2B Burnski, Robbie Doherty, Tommy Phillips and Pirate Copy.",
  },
  {
    question: "What kind of music is Kaluki known for?",
    answer: "Kaluki is known for house, tech house, rolling groove and club-focused underground dance music.",
  },
  {
    question: "Can HLT help with hotels and concierge for Kaluki Ibiza?",
    answer: "Yes. HLT can help with Ibiza hotels, villas, transfers, tickets, VIP extras, after-party support and concierge planning around Kaluki at 528 Ibiza.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Kaluki at 528 Ibiza 2026: The Open-Air Tech House Party to Build Your Sunday Around",
  description:
    "Kaluki lands at 528 Ibiza on Sunday 31 May 2026 with Julian Fijma, Kepler B2B Burnski, Robbie Doherty, Tommy Phillips and Pirate Copy.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-05-31",
  dateModified: "2026-05-31",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "Kaluki at 528 Ibiza",
  startDate: "2026-05-31T17:00:00+02:00",
  endDate: "2026-05-31T23:00:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "528 Ibiza",
    address: "Ibiza, Spain",
  },
  performer: LINEUP.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "GBP",
    price: "41.58",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Book Kaluki 528 Ibiza Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Plan Kaluki with HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function Kaluki528IbizaArticle() {
  return (
    <>
      <Helmet>
        <title>Kaluki 528 Ibiza 2026 Tickets | Sunday 31 May Open-Air Party</title>
        <meta
          name="description"
          content="Book Kaluki at 528 Ibiza on Sunday 31 May 2026. Lineup includes Julian Fijma, Kepler B2B Burnski, Robbie Doherty, Tommy Phillips and Pirate Copy. Plan Ibiza tickets, hotels and concierge with HLT."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Kaluki 528 Ibiza 2026 Tickets | Sunday 31 May Open-Air Party" />
        <meta property="og:description" content="Kaluki brings Manchester-rooted tech house energy to 528 Ibiza with Julian Fijma, Kepler B2B Burnski, Robbie Doherty, Tommy Phillips and Pirate Copy." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kaluki 528 Ibiza 2026 Tickets" />
        <meta name="twitter:description" content="Plan Kaluki at 528 Ibiza with HLT tickets, hotels, VIP extras and concierge support." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">Ibiza 2026 guide</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              Kaluki at 528 Ibiza 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Kaluki brings Manchester-rooted tech house energy to 528 Ibiza on Sunday 31 May 2026, with Julian Fijma, Kepler B2B Burnski, Robbie Doherty, Tommy Phillips and Pirate Copy.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta />
              <ConciergeCta children="Build the Ibiza Sunday with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>Kaluki</dd></div>
              <div><dt className="text-white">Venue</dt><dd>528 Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Sunday 31 May 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>17:00 - 23:00</dd></div>
              <div><dt className="text-white">Starting price</dt><dd>From GBP 41.58</dd></div>
              <div><dt className="text-white">Extra detail</dt><dd>Includes complimentary shuttle bus service to San Antonio bus station after the party</dd></div>
            </dl>
          </aside>
        </header>

        <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden border border-brand-500/25 bg-black shadow-[0_24px_90px_rgba(0,0,0,0.55)] transition hover:border-brand-500/70">
          <img src="/assets/community/8273b509-c413-4b18-89fc-059814fb796c.png" alt="Kaluki at 528 Ibiza event poster" className="block h-auto w-full transition duration-500 group-hover:scale-[1.01]" />
        </a>

        <div className="mx-auto max-w-4xl space-y-10">
          <section className="space-y-4 text-base leading-8 text-slate-300">
            <p>
              Kaluki at 528 Ibiza is the kind of Sunday event that makes sense for HLT: open-air, house-focused, easy to build a trip around and connected to a brand that already has strong credibility with UK and European clubbers.
            </p>
            <p>
              The Sunday 31 May 2026 edition runs from 17:00 to 23:00 at 528 Ibiza, giving travellers a golden-hour start, a proper evening dancefloor and enough time to continue the night elsewhere if the group wants to keep moving.
            </p>
            <p>
              If you are building an Ibiza weekend around house music, Kaluki is one of the cleaner choices. It gives you a clear destination, a strong lineup, a recognisable sound and a ticket route that can anchor the rest of your plans.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="Book Kaluki 528 Ibiza tickets" />
              <ConciergeCta children="Ask HLT to build the full Ibiza plan" />
            </div>
          </section>

          <ArticleSection title="Why Kaluki fits Ibiza so well">
            <p>
              Kaluki has long been associated with the UK tech house lane: rolling basslines, strong percussion, party energy and lineups that speak to people who know the club scene rather than casual nightlife browsers.
            </p>
            <p>
              Ibiza works best when the event has a real identity. Kaluki has that. It is not just another logo on a calendar. It is a music-led brand with a clear crowd, a clear tempo and a sound that works naturally in an open-air Ibiza setting.
            </p>
            <p>
              That is why the 528 Ibiza setting matters. A daytime-to-evening slot changes the feel of the whole trip. You can make it part of a Sunday plan: pool, beach, hotel, transfer, Kaluki, then afters or a quieter finish depending on the group.
            </p>
          </ArticleSection>

          <ArticleSection title="The lineup: Julian Fijma, Kepler B2B Burnski and more">
            <p>
              This lineup is built for dancers who want groove first. Julian Fijma brings a modern underground house sound with clean drums, movement and DJ-friendly energy. Kepler B2B Burnski adds a deeper, rolling angle that should suit the open-air slot perfectly.
            </p>
            <p>
              Robbie Doherty, Tommy Phillips and Pirate Copy round out the bill with UK and European club energy. It is a lineup that feels shaped for a proper Sunday session rather than a single headline moment.
            </p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              {LINEUP.map((artist) => <li key={artist}>{artist}</li>)}
            </ul>
          </ArticleSection>

          <ArticleSection title="Why 528 Ibiza is a strong venue for this event">
            <p>
              528 Ibiza gives Kaluki space to breathe. Instead of a dark-room-only club experience, the venue gives the party a bigger outdoor feeling, which is exactly what many people want from an Ibiza Sunday.
            </p>
            <p>
              The timing also helps. A 17:00 start means you are not waiting until midnight to feel like the trip has begun. You can get dressed earlier, move as a group, arrive in daylight and watch the event shift into night.
            </p>
            <p>
              For a travel-led audience, that is useful. It makes transport easier, gives the group more structure and creates a natural anchor for the day.
            </p>
          </ArticleSection>

          <ArticleSection title="Build your Ibiza trip around Kaluki">
            <p>
              Some people will only need the ticket. Others will want the entire Sunday and the surrounding weekend planned properly. That is where HLT comes in.
            </p>
            <p>Through HLT, you can build around Kaluki at 528 Ibiza with:</p>
            <ul className="list-disc space-y-2 pl-6 text-slate-200">
              <li>Kaluki 528 Ibiza ticket access</li>
              <li>Hotels near San Antonio, Ibiza Town, Talamanca or Playa d'en Bossa</li>
              <li>Group villas for private pool days and pre-party plans</li>
              <li>Airport transfers and event transport</li>
              <li>VIP extras, restaurants, beach clubs and after-party enquiries</li>
              <li>Full Ibiza packages combining tickets, hotels, transfers and concierge support</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="View Kaluki ticket options" />
              <ConciergeCta children="Request an Ibiza concierge package" />
              <Link to={EVENTS_URL} className="btn-secondary">Browse more Ibiza events</Link>
            </div>
          </ArticleSection>

          <ArticleSection title="Where to stay for Kaluki at 528 Ibiza">
            <p>
              If Kaluki is the centrepiece, San Antonio is the easiest practical base, especially because the event listing mentions complimentary shuttle bus service to San Antonio bus station after the party.
            </p>
            <p>
              Ibiza Town and Talamanca work better if you want restaurants, marina energy and a more polished hotel stay. Playa d'en Bossa works if you are combining Kaluki with Hi Ibiza, Ushuaia, beach clubs and a bigger nightlife schedule.
            </p>
            <p>
              For groups, a villa can be the cleanest option. It gives you privacy, pool time, space to recover and a proper base for the weekend rather than moving between hotel rooms and taxis all day.
            </p>
            <ConciergeCta children="Ask HLT where to stay for Kaluki" />
          </ArticleSection>

          <ArticleSection title="Who should go?">
            <p>
              Kaluki at 528 Ibiza is ideal for people who love tech house, house, rolling groove, UK club culture and open-air Ibiza parties. It is especially strong for groups who want a daytime-to-evening event that can start the night properly without needing to wait until the early hours.
            </p>
            <p>
              It also works for travellers who want an Ibiza event that feels current, music-led and social. If your group wants more than a random night out, Kaluki gives the day a centrepiece.
            </p>
          </ArticleSection>

          <ArticleSection title="SEO travel angle: why this event matters for Ibiza trips">
            <p>
              More people are searching for Ibiza house music events before they book flights and hotels. They want the event first, then the accommodation, then the rest of the trip. Kaluki at 528 Ibiza fits that behaviour perfectly.
            </p>
            <p>
              Search terms like Kaluki Ibiza 2026, Kaluki 528 Ibiza tickets, 528 Ibiza events, Ibiza tech house events and Ibiza open-air parties all point to the same user intent: people want a clear event, a trusted ticket route and help building the weekend around it.
            </p>
            <p>
              HLT exists for that exact customer. We help turn the event into a proper plan: tickets, hotels, villas, transfers, VIP extras, after parties and concierge support for groups that want the easy route.
            </p>
          </ArticleSection>

          <ArticleSection title="Final word">
            <p>
              Kaluki at 528 Ibiza on Sunday 31 May 2026 is a strong HLT spotlight event because it has the right ingredients: a trusted house music brand, a strong open-air venue, a focused lineup and a Sunday slot that can shape the whole trip.
            </p>
            <p>
              Book the ticket, plan the hotel and build the rest around the event. Ibiza is easier when the music leads the itinerary.
            </p>
            <div className="flex flex-wrap gap-3">
              <TicketCta children="Book Kaluki tickets" />
              <ConciergeCta children="Build the trip with HLT" />
            </div>
          </ArticleSection>

          <section className="border-t border-white/10 pt-8">
            <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
            <div className="mt-5 grid gap-3">
              {FAQS.map((item) => (
                <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                  <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                  <p className="mt-3 leading-7 text-slate-300">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
